import React, { useState, useEffect } from 'react';
import { Palette } from 'lucide-react';

export type PlatformTheme = 'monarch-dark' | 'pure-light' | 'cyber-purple' | 'emerald-fintech';

export const THEME_LIST: { id: PlatformTheme; name: string; desc: string; badge: string; dot: string }[] = [
  { id: 'monarch-dark', name: 'Monarch Noir', desc: 'Суверенный глубокий фиолетово-чёрный', badge: 'Dark', dot: 'bg-purple-500' },
  { id: 'pure-light', name: 'Academic White', desc: 'Светлая тема с мягкими тенями', badge: 'Light', dot: 'bg-slate-300' },
  { id: 'cyber-purple', name: 'Cyber Neon', desc: 'Неоновый пурпур и киберпанк акценты', badge: 'Neon', dot: 'bg-fuchsia-500' },
  { id: 'emerald-fintech', name: 'Emerald FinTech', desc: 'Банковский изумруд и золото инвестиций', badge: 'FinTech', dot: 'bg-emerald-500' }
];

export const ThemeSwitcher = () => {
  const [theme, setTheme] = useState<PlatformTheme>('monarch-dark');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const saved = (localStorage.getItem('study_platform_theme') as PlatformTheme) || 'monarch-dark';
    applyTheme(saved);
  }, []);

  const applyTheme = (t: PlatformTheme) => {
    setTheme(t);
    localStorage.setItem('study_platform_theme', t);
    const root = document.documentElement;

    root.classList.remove('theme-monarch-dark', 'theme-pure-light', 'theme-cyber-purple', 'theme-emerald-fintech', 'dark');

    if (t === 'pure-light') {
      root.classList.remove('dark');
      root.classList.add('theme-pure-light');
      document.body.style.backgroundColor = '#f8fafc';
      document.body.style.color = '#0f172a';
    } else if (t === 'cyber-purple') {
      root.classList.add('dark', 'theme-cyber-purple');
      document.body.style.backgroundColor = '#0b001a';
      document.body.style.color = '#ffffff';
    } else if (t === 'emerald-fintech') {
      root.classList.add('dark', 'theme-emerald-fintech');
      document.body.style.backgroundColor = '#02140e';
      document.body.style.color = '#ffffff';
    } else {
      root.classList.add('dark', 'theme-monarch-dark');
      document.body.style.backgroundColor = '#07030e';
      document.body.style.color = '#ffffff';
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="glass-btn-secondary p-2.5 rounded-xl text-white flex items-center justify-center gap-1.5 hover:scale-105 active:scale-95 transition-transform"
        title="Сменить тему оформления"
      >
        <Palette className="w-4 h-4 text-white" />
      </button>

      {isOpen && (
        <div className="absolute bottom-12 left-0 sm:left-auto sm:right-0 z-50 p-2.5 rounded-2xl glass-panel min-w-[240px] space-y-1.5 shadow-2xl border border-white/20 animate-in fade-in zoom-in-95 duration-200">
          <div className="px-2 py-1 text-[10px] uppercase font-black tracking-widest text-white/40 border-b border-white/10 mb-1 flex items-center justify-between">
            <span>Темы интерфейса</span>
            <span className="text-[9px] font-mono lowercase">v2.5</span>
          </div>
          {THEME_LIST.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => { applyTheme(item.id); setIsOpen(false); }}
              className={`w-full text-left p-2.5 rounded-xl transition-all flex items-start gap-3 ${
                theme === item.id 
                  ? 'glass-btn-primary shadow-lg shadow-white/20' 
                  : 'hover:bg-white/10 text-white/80'
              }`}
            >
              <div className={`w-3 h-3 rounded-full mt-0.5 flex-shrink-0 ${item.dot} shadow-sm`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-bold leading-tight truncate">{item.name}</span>
                  <span className="text-[9px] px-1.5 py-0.2 rounded-md bg-white/10 font-mono">{item.badge}</span>
                </div>
                <div className={`text-[10px] line-clamp-1 mt-0.5 ${theme === item.id ? 'text-[#07030e]/70 font-medium' : 'text-white/40'}`}>
                  {item.desc}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
